import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Clock,
  User as UserIcon,
  Calendar,
  AlertTriangle,
  CheckCircle,
  FolderOpen,
  MessageCircle,
  History as HistoryIcon,
} from 'lucide-react';
import TaskComments from '@/components/task-comments';
import { useUnreadComments } from '@/contexts/UnreadCommentsContext';
import TaskHistoryModal from '@/components/task-history-modal';
import TimeTracker from '@/components/time-tracker';
import type { TaskWithRelations, User } from '@shared/schema';

interface TaskViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: TaskWithRelations | null;
  currentUser?: User;
  requestExtension?: boolean; // If true, opens comments in extension request mode
}

export default function TaskViewModal({
  isOpen,
  onClose,
  task,
  currentUser,
  requestExtension = false,
}: TaskViewModalProps) {
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const { markTaskCommentsAsRead } = useUnreadComments();

  // Common assignees for shared/common tasks (safe even if task is null)
  const taskId = task?.id;
  const { data: commonAssignees = [] } = useQuery<any[]>({
    queryKey: ['/api/tasks', taskId, 'assignees'],
    enabled: !!taskId,
  });

  if (!task) return null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      case 'started':
        return 'bg-blue-100 text-blue-800';
      case 'working':
        return 'bg-yellow-100 text-yellow-800';
      case 'need_more_time':
        return 'bg-orange-100 text-orange-800';
      case 'need_help':
        return 'bg-red-100 text-red-800';
      case 'complete':
        return 'bg-green-100 text-green-800';
      case 'reopened':
        return 'bg-amber-100 text-amber-800';
      case 'dropped':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getOverdueDeadline = () => {
    const dueDate = new Date(task.dueDate);
    const deadline = new Date(dueDate);
    // Consider overdue only after the next day starts (grace period through due date)
    deadline.setDate(deadline.getDate() + 1);
    deadline.setHours(0, 0, 0, 0);
    return deadline;
  };

  const isUrgent = () => {
    if (!task.dueDate) return false;
    const now = new Date();
    const deadline = getOverdueDeadline();
    const diffHours = (deadline.getTime() - now.getTime()) / (1000 * 60 * 60);
    return diffHours <= 24 && diffHours > 0;
  };

  const isPastDue = () => {
    if (!task.dueDate) return false;
    const now = new Date();
    const deadline = getOverdueDeadline();
    return deadline < now;
  };

  const canTrackTime =
    currentUser &&
    (task.assignedTo === currentUser.id ||
      currentUser.role === 'manager' ||
      currentUser.role === 'admin');

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-full max-w-3xl md:max-w-4xl lg:max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <DialogTitle
                  className="text-xl font-semibold mb-2"
                  data-testid="task-view-title"
                >
                  {task.title}
                </DialogTitle>
                <div className="flex items-center space-x-3 text-sm text-gray-600">
                  <Badge className={getStatusColor(task.status)}>
                    {task.status.replace('_', ' ')}
                  </Badge>
                  {isUrgent() && (
                    <Badge variant="destructive" className="animate-pulse">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Urgent
                    </Badge>
                  )}
                  {isPastDue() && (
                    <Badge variant="destructive">
                      <Clock className="w-3 h-3 mr-1" />
                      Past Due
                    </Badge>
                  )}
                  {task.status === 'complete' && (
                    <Badge className="bg-green-100 text-green-800">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Completed
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-6">
                {/* Description */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Description</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p
                      className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap"
                      data-testid="task-description"
                    >
                      {task.description || 'No description provided.'}
                    </p>
                  </CardContent>
                </Card>

                {/* Comments Section */}
                <TaskComments
                  taskId={task.id}
                  currentUser={currentUser}
                  requestExtension={requestExtension}
                  task={task}
                  onDueDateExtended={onClose}
                  onCommentsViewed={() => markTaskCommentsAsRead(task.id)}
                />
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Task Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Task Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Assignee */}
                    <div className="flex items-center space-x-3">
                      <UserIcon className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium">Assigned to</p>
                        {task.assignee ? (
                          <div className="flex items-center space-x-2 mt-1">
                            <Avatar className="w-6 h-6">
                              <AvatarImage
                                src={task.assignee.profileImageUrl || undefined}
                                alt={`${task.assignee.firstName} ${task.assignee.lastName}`}
                              />
                              <AvatarFallback className="text-xs">
                                {task.assignee.firstName?.[0]}
                                {task.assignee.lastName?.[0]}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm">
                              {task.assignee.firstName} {task.assignee.lastName}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">
                            Unassigned
                          </span>
                        )}
                        {Array.isArray(commonAssignees) &&
                          commonAssignees.length > 1 && (
                            <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                              Shared with:{' '}
                              {commonAssignees
                                .filter(
                                  (u: any) => u.id !== task.assignedTo
                                )
                                .map(
                                  (u: any) =>
                                    `${u.firstName || ''} ${u.lastName || ''}`.trim()
                                )
                                .join(', ')}
                            </p>
                          )}
                      </div>
                    </div>

                    <Separator />

                    {/* Project */}
                    {task.project && (
                      <>
                        <div className="flex items-center space-x-3">
                          <FolderOpen className="w-4 h-4 text-gray-500" />
                          <div>
                            <p className="text-sm font-medium">Project</p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                              {task.project.name}
                              {task.project.hasAmc &&
                                task.project.amcExpiryDate && (
                                  <span className="ml-2 text-xs opacity-75 block">
                                    AMC expires:{' '}
                                    {new Date(
                                      task.project.amcExpiryDate
                                    ).toLocaleDateString()}
                                  </span>
                                )}
                              {task.project.supportPackageValidityPeriod && (
                                <span className="ml-2 text-xs opacity-75 block">
                                  Support expires:{' '}
                                  {new Date(
                                    task.project.supportPackageValidityPeriod
                                  ).toLocaleDateString()}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        <Separator />
                      </>
                    )}

                    {/* Due Date */}
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium">Due Date</p>
                        <p
                          className={`text-sm ${isPastDue() ? 'text-red-600' : isUrgent() ? 'text-orange-600' : 'text-gray-700 dark:text-gray-300'}`}
                        >
                          {new Date(task.dueDate).toLocaleDateString('en-US', {
                            weekday: 'short',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </p>
                      </div>
                    </div>

                    <Separator />

                    {/* Created By */}
                    <div className="flex items-center space-x-3">
                      <UserIcon className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium">Created by</p>
                        {task.creator ? (
                          <div className="flex items-center space-x-2 mt-1">
                            <Avatar className="w-6 h-6">
                              <AvatarImage
                                src={task.creator.profileImageUrl || undefined}
                                alt={`${task.creator.firstName} ${task.creator.lastName}`}
                              />
                              <AvatarFallback className="text-xs">
                                {task.creator.firstName?.[0]}
                                {task.creator.lastName?.[0]}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm">
                              {task.creator.firstName} {task.creator.lastName}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">Unknown</span>
                        )}
                      </div>
                    </div>

                    <Separator />

                    {/* Created Date */}
                    <div className="flex items-center space-x-3">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium">Created</p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {new Date(task.createdAt!).toLocaleDateString(
                            'en-US',
                            {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            }
                          )}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Time Tracking */}
                {canTrackTime && (
                  <TimeTracker task={task} canTrackTime={canTrackTime!} />
                )}

                {/* Task Actions */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      <button
                        onClick={() => setIsHistoryOpen(true)}
                        className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center text-sm"
                        data-testid="button-view-history"
                      >
                        <HistoryIcon className="w-4 h-4 mr-2 text-blue-600" />
                        <span className="text-blue-600">View Task History</span>
                      </button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Task History Modal */}
      <TaskHistoryModal
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        taskId={task.id}
        taskTitle={task.title}
      />
    </>
  );
}
